import { getStoredAuthToken, getStoredRefreshToken } from './authStorage';
import { performRefresh } from '../services/apiClient';

const REFRESH_THRESHOLD = 0.8;

let refreshTimer = null;
let isRefreshing = false;

const decodeJwtPayload = (token) => {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload);
    return JSON.parse(decoded);
  } catch (_e) {
    return null;
  }
};

const performProactiveRefresh = async () => {
  if (isRefreshing) return;
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return;

  isRefreshing = true;
  try {
    // Use the shared performRefresh from apiClient.js so that proactive
    // and reactive (401 interceptor) refresh calls are deduplicated via
    // the refreshPromise singleton. Without this, both could fire
    // concurrently when the page loads with an expired JWT, making two
    // duplicate POST /api/users/refresh requests.
    const newToken = await performRefresh(refreshToken);
    if (newToken) {
      scheduleProactiveRefresh();
    }
  } catch (_e) {
    // Transient failure — the reactive 401 interceptor will handle it
    // when the token eventually expires. No need to log the user out here.
  } finally {
    isRefreshing = false;
  }
};

export const scheduleProactiveRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  const token = getStoredAuthToken();
  if (!token) {
    // No JWT — but if we have a refresh token, attempt to get a new JWT.
    // This handles the case where a transient refresh failure on a previous
    // page load cleared the JWT via clearAuthTokenOnly() but left the
    // refresh token intact. Without this, the proactive refresh manager
    // would never fire again, leaving the user stuck in a logged-out state.
    const refreshToken = getStoredRefreshToken();
    if (refreshToken) {
      performProactiveRefresh();
    }
    return;
  }

  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return;

  const expiryMs = payload.exp * 1000;
  const iatMs = payload.iat ? payload.iat * 1000 : (expiryMs - 3600000);
  const now = Date.now();
  const lifetime = expiryMs - iatMs;
  const refreshAt = iatMs + (lifetime * REFRESH_THRESHOLD);
  const delay = refreshAt - now;

  if (delay <= 0) {
    // Already past refresh threshold (or token expired) — refresh now.
    // The isRefreshing guard in performProactiveRefresh prevents concurrent calls.
    performProactiveRefresh();
    return;
  }

  refreshTimer = setTimeout(() => {
    performProactiveRefresh();
  }, Math.min(delay, 2147483647)); // Clamp to max 32-bit int for setTimeout
};

const handleVisibilityChange = () => {
  if (document.visibilityState !== 'visible') return;

  const token = getStoredAuthToken();
  if (!token) {
    // No JWT — if we have a refresh token, try to get a new JWT.
    // Same rationale as in scheduleProactiveRefresh.
    const refreshToken = getStoredRefreshToken();
    if (refreshToken) {
      performProactiveRefresh();
    }
    return;
  }

  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return;

  const expiryMs = payload.exp * 1000;
  const iatMs = payload.iat ? payload.iat * 1000 : (expiryMs - 3600000);
  const now = Date.now();
  const lifetime = expiryMs - iatMs;
  const remaining = expiryMs - now;

  if (remaining <= 0) {
    // Token is already expired — attempt a refresh using the refresh token.
    // The reactive 401 interceptor in apiClient.js would also handle this on
    // the next API call, but proactively refreshing here avoids a failed
    // request and gives a smoother UX when returning to a backgrounded tab.
    performProactiveRefresh();
  } else if (remaining < lifetime * (1 - REFRESH_THRESHOLD)) {
    // Less than 20% of lifetime remains — refresh immediately
    performProactiveRefresh();
  } else {
    // Reschedule timer for the existing token
    scheduleProactiveRefresh();
  }
};

export const initProactiveRefresh = () => {
  scheduleProactiveRefresh();
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }
};

export const cleanupProactiveRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }
};
